import Button from "../../../components/Button/Button"
import { useEffect, useRef, useState } from "react"
import dayjs from "dayjs"
import { editNewLeadDetails } from "../../../features/contacts/contacts"
import { useDispatch } from "react-redux"
import { showSnackBar } from "../../../components/Snackbar/Snackbar"
import { calculateMonthlyPaymentAmount, calculateTotalAmount, formatEnrolledTotal, lockScheduleAndReviewTabs, pickProgramPlanTier, sumSelectedBalances, validateMinimumMonthlyPayment } from "./helper"
import { StorageGetItem, USER } from "../../../lib/storage"
import { dateToServer } from "../../../utils/date"

import { getFrequencyBasedProgramLength, getFDRMinimumPayment, formatMinimumPayment, FDR_ONE_PERCENT_THRESHOLD, FDR_PAYMENT_TIERS, PAYMENT_SCHEDULE, paymentsLabel } from "../../../config/consts"
import Input from "../../../components/Input/Input"
import Dropdown from "../../../components/Dropdown/Dropdown"
import Calendar from "../../../components/Calendar/Calendar"
import { ProgramFee, programFeeValues } from "../../../components/ProgramFee/ProgramFee"
import FeeShapeChoice from "../../../components/FeeShape/FeeShapeChoice"
import { useFlatFeeText } from "../../../components/FeeShape/flatFeeInput"
import { flatFeeMenuItems } from "../../../components/FeeShape/flatFeeMenu"
import { CustodialFee } from "../../../components/CustodialFee/CustodialFee"
import ToolTip from "../../../components/ToolTip/ToolTip"
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal"
import { programPlanInit } from "./consts"
import { isProgramPlanIncomplete, applyFeeShapePick } from "./enrolmentGates"

// The fee shape a program plan is saved with, and keeps when its state is written back.
//
// ONE rule for both. They disagreed: the request sent `{ feeType: 'flat', flatFee: null }` for a rep
// who picked Flat and never opened the amount dropdown, while the write-back said percent. The radio
// jumped back on its own while the lead on the server read flat with no amount, which prices at $0.
// A flat pick without an amount is not a flat pick yet.
//
// updateProgramPlan rebuilds local state from an object literal after saving. That literal listed
// every field except these two, so the rep's pick was dropped the moment they hit Save — and a
// second save then sent percentage, re-pricing a client who had been agreed a flat fee. Same defect
// as the back-office re-open bug, reached without even reloading the page.
export const buildSavedFeeShape = (programPlan) => {
  const amount = Number(programPlan?.flatFee)

  return programPlan?.feeType === 'flat' && amount > 0
    ? { feeType: 'flat', flatFee: amount }
    : { feeType: 'percent', flatFee: null }
}

// The same answer, named for the place it is used — the state write-back after a save.
export const keepFeeShape = buildSavedFeeShape

const SPLIT_WOULD_DROP = 'cardSplitWouldDrop'

export const ProgramPlan = ({
  programPlan,
  setProgramPlan,
  paymentSchedule,
  setPaymentSchedule,
  setDisableTabs,
  setSelectedTab,
  userId,
  selectedCreditLines,
  contactGroupConfig,
  leadCreatorId,
  pendingTierReapplyRef,
  manualTierEditsRef,
  recalcRequestedRef,
  flatFeeTyped = false,
  lowerFeeFloor = false,
  debtSummary = false,
  onePercentFloor = false,
}) => {

  const dispatch = useDispatch()
  const [reqError, setReqError] = useState('')
  // WS #2228897: the server's message while the rep decides whether a fee change may drop the card split.
  const [splitDrop, setSplitDrop] = useState(null)
  const [userRole, setUserRole] = useState('')
  const [isAffiliateManager, setIsAffiliateManager] = useState(false)
  const [currentUserId, setCurrentUserId] = useState(null)
  const isInitialFeeDisabled = userRole === 'affiliate' && contactGroupConfig?.disableInitialFee === true

  // What this contact's office permits for the program fee, and what the rep picked for THIS client.
  // No role gate: the rep is the person who agrees the number with the client, which is the whole
  // point of having it here rather than only in the back-office regenerate-plan modal.
  const feeShape = contactGroupConfig?.feeShape
  const flatAllowed = !!feeShape?.allowed
  // WS #2219702: a flat-fee-only office (PACK, Hayden). Decided by the server, flag included.
  const flatOnly = flatAllowed && feeShape?.flatOnly === true
  // WS #2346323: the % list from 15% (flag PROGRAM_FEE_FLOOR_15, read by AddContact), only where the
  // office permits a flat fee: the server skips the % range check exactly there.
  const lowerFloor = lowerFeeFloor && flatAllowed
  const isFlat = programPlan?.feeType === 'flat'
  // Sent whenever a flat fee is in play, so a client already on one keeps it even if the office
  // permission is later withdrawn. The server re-checks the permission and refuses a pick it does
  // not allow, so sending it is always safe.
  const sendsFeeShape = flatAllowed || isFlat

  const flatFeeMenu = flatFeeMenuItems({ options: feeShape?.options, current: programPlan?.flatFee })

  const pickFeeShape = (nextType) => {
    markRecalcRequested()
    // Also starts a flat pick at one payment, and restores the previous length on the way back —
    // see applyFeeShapePick.
    manualEdits.current.plan = true
    setProgramPlan((prev) => applyFeeShapePick({ prev, nextType }))
  }

  // A flat-only office has no Percentage to offer, so the plan starts on Flat, the same change as
  // the rep clicking it (one payment). A plan already on Flat is left alone, length included.
  useEffect(() => {
    if (flatOnly && programPlan && programPlan.feeType !== 'flat') pickFeeShape('flat')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatOnly, programPlan?.feeType])

  const [frequency, setFrequency] = useState(programPlan?.frequency || 'monthly')
  const [showMinimumPaymentWarning, setShowMinimumPaymentWarning] = useState(false)
  const startDate = paymentSchedule?.cycleStartDate
  const secondStartDate = paymentSchedule?.secondDate
  const hasStartDate = startDate && dayjs(startDate).isValid()

  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role)
      setIsAffiliateManager(result?.isAffiliateManager || false)
      setCurrentUserId(result?._id || null)
    })

  }, [])

  const totalBalanceOwed = sumSelectedBalances(selectedCreditLines)

  const minimumPayment = getFDRMinimumPayment(totalBalanceOwed, { onePercentFloor })
  const onePercentSetsMinimum = minimumPayment > getFDRMinimumPayment(totalBalanceOwed)

  const defaults = contactGroupConfig?.programPlanDefaults
  const defaultsEnabled = defaults?.enabled === true
  const matchedTier = defaultsEnabled
    ? pickProgramPlanTier({ tiers: defaults?.tiers, totalDebt: totalBalanceOwed })
    : null
  const defaultProgramFee = matchedTier ? matchedTier.programFee : null
  const defaultCustodialFee = matchedTier ? matchedTier.custodialFee : null
  const defaultProgramLength = matchedTier ? matchedTier.programLength : null

  const computeProgramFeeMenuValues = () => programFeeValues({ role: userRole, isAffiliateManager, lowerFloor })

  const snapToNearest = (value, options) => {
    if (value == null || !Array.isArray(options) || options.length === 0) return null
    return options.reduce((best, cur) => Math.abs(cur - value) < Math.abs(best - value) ? cur : best)
  }

  const localManualEdits = useRef({ programFee: false, custodialFee: false, plan: false })
  const localPendingReapply = useRef(false)
  const manualEdits = manualTierEditsRef || localManualEdits
  const pendingReapply = pendingTierReapplyRef || localPendingReapply

  const markRecalcRequested = () => {
    if (recalcRequestedRef) recalcRequestedRef.current = true
    if (setDisableTabs) setDisableTabs(lockScheduleAndReviewTabs)
  }

  // The typed flat fee (WS #1944843, flag FLAT_FEE_TYPED_AMOUNT read by AddContact). The plan only ever
  // holds a valid number or null, which is what keeps Save closed on a bad entry.
  const flatFeeField = useFlatFeeText({
    amount: programPlan?.flatFee ?? null,
    max: feeShape?.max,
    onAmount: (flatFee) => {
      markRecalcRequested()
      setProgramPlan((prev) => ({ ...prev, flatFee }))
    },
  })

  useEffect(() => {
    if (!defaultsEnabled) return
    if (!userRole) return
    const force = pendingReapply.current === true
    setProgramPlan((prev) => {
      const next = { ...prev }
      const canApply = (val, init, key) => {
        if (manualEdits.current?.[key] === true) return false
        if (val === init || val === '' || val === null || val === undefined) return true
        return force
      }

      if (defaultProgramFee != null && canApply(prev?.programFee, programPlanInit.programFee, 'programFee')) {
        const snapped = snapToNearest(defaultProgramFee, computeProgramFeeMenuValues())
        if (snapped != null) next.programFee = snapped
      }
      if (defaultCustodialFee != null && canApply(prev?.custodialFee, programPlanInit.custodialFee, 'custodialFee')) {
        const snapped = snapToNearest(defaultCustodialFee, [0, 50, 100])
        if (snapped != null) next.custodialFee = snapped
      }
      if (defaultProgramLength != null && canApply(prev?.plan, programPlanInit.plan, 'plan')) {
        const lengthOptions = getFrequencyBasedProgramLength(frequency, totalBalanceOwed, userRole, isAffiliateManager).map(o => o.value)
        const snapped = snapToNearest(defaultProgramLength, lengthOptions)
        if (snapped != null) next.plan = snapped
      }
      return next
    })
    if (force) pendingReapply.current = false
  }, [defaultsEnabled, defaultProgramFee, defaultCustodialFee, defaultProgramLength, userRole, isAffiliateManager, lowerFloor, frequency, totalBalanceOwed])

  useEffect(() => {
    if (isInitialFeeDisabled) {
      setProgramPlan((prev) => ({ ...prev, initialFee: 0 }))
    }
  }, [isInitialFeeDisabled])

  const getProgramLengthMenu = () => {
    const baseOptions = getFrequencyBasedProgramLength(frequency, totalBalanceOwed, userRole, isAffiliateManager)

    if (programPlan?.plan && !baseOptions.some(opt => opt.value === programPlan.plan)) {
      return [...baseOptions, {
        label: paymentsLabel(programPlan.plan),
        value: programPlan.plan
      }].sort((a, b) => a.value - b.value)
    }

    return baseOptions
  }

  useEffect(() => {
    if (programPlan?.plan && totalBalanceOwed > 0) {
      const isValid = validateMinimumMonthlyPayment({
        accountBalance: totalBalanceOwed,
        programPlan,
        programLength: programPlan.plan,
        frequency,
        startDate,
        secondStartDate,
        onePercentFloor,
      })
      setShowMinimumPaymentWarning(!isValid)
    }
    // feeType and flatFee belong here: calculateProgramFeeAmount prices a flat plan from flatFee,
    // so naming the amount changes the answer exactly as changing programFee does. Without them
    // the screen kept warning "below minimum" on a plan of one $4,500 payment, telling the rep to
    // fix a plan that was already fine.
  }, [programPlan?.plan, frequency, totalBalanceOwed, programPlan?.programFee, programPlan?.custodialFee, programPlan?.feeType, programPlan?.flatFee, startDate, secondStartDate, onePercentFloor])

  useEffect(() => {
    if (programPlan?.frequency && programPlan.frequency !== frequency) {
      setFrequency(programPlan.frequency)
    }
  }, [programPlan?.frequency])

  useEffect(() => {
    const handleReqError = () => {
      if (reqError.length > 0) {
        showSnackBar("error", reqError)
        setReqError('')
      }
    }

    handleReqError()
  }, [reqError])

  // Resolves false when the save is waiting for the rep to confirm dropping the card split.
  const updateProgramPlan = async ({ dropSplit = false } = {}) => {
    const postData = {
      programPlan: {
        programLength: programPlan?.plan,
        programFee: programPlan?.programFee,
        // Omitted entirely on an ordinary percentage office — every office today — so the request
        // is byte for byte what it always was.
        ...(sendsFeeShape ? buildSavedFeeShape(programPlan) : {}),
        custodialFee: programPlan?.custodialFee,
        initialFee: programPlan?.initialFee,
        amount: totalBalanceOwed,
        ...(dropSplit ? { cardSplit: [] } : {}),
      },
      paymentSchedule: {
        frequency: frequency,
        startDate: dateToServer(paymentSchedule?.cycleStartDate),
      }
    }
    try {
      const response = await dispatch(editNewLeadDetails({ userId, data: postData }))
      setProgramPlan({
        plan: programPlan?.plan,
        programFee: programPlan?.programFee,
        custodialFee: programPlan?.custodialFee,
        amount: totalBalanceOwed,
        initialFee: programPlan?.initialFee,
        frequency: frequency,
        ...keepFeeShape(programPlan),
      }
      )
      if (response.error && response.meta?.code === SPLIT_WOULD_DROP) {
        setSplitDrop(response.payload)
        return false
      }
      if (response.error) {
        setReqError(response.payload)
      } else {
        showSnackBar("success", "Successfully")
      }
    } catch (error) {
      setReqError('An unexpected error occurred. Please try again.')
    }
    return true
  }

  const proceed = () => {
    if (recalcRequestedRef) recalcRequestedRef.current = true
    setDisableTabs((prev) => ({ ...prev, paymentSchedule: false, }))
    setSelectedTab("payment-schedule")
  }

  return (
    <>
      <div className="bg-surface border border-divider shadow-lg rounded-2xl w-full max-w-[798px]">
        <div className="flex justify-between">
          <div className="font-bold text-xl-20 pt-[24px] pl-[48px] pb-[16px]">
            Program Plan
          </div>
          <div className="flex pr-8 items-center gap-2 text-text-secondary font-semibold">
            {debtSummary
              ? <span>Enrolled: {formatEnrolledTotal(selectedCreditLines)}</span>
              : <span>Enrolled: ${totalBalanceOwed?.toFixed(2)}</span>}
            <ToolTip>
              <div className="text-xs">
                <div className="font-bold mb-2 text-text-primary">FDR Minimum Payment Requirements</div>
                <div className="space-y-1">
                  {FDR_PAYMENT_TIERS.map((tier, index) => {
                    const enrolledAmount = Number(totalBalanceOwed) || 0
                    const isCurrentTier = enrolledAmount >= tier.min && (tier.max === Infinity || enrolledAmount < tier.max)
                    return (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: isCurrentTier ? 'var(--color-blue-100)' : 'transparent',
                          fontWeight: isCurrentTier ? '600' : '400',
                          color: isCurrentTier ? 'var(--color-blue-700)' : 'var(--color-gray-600)'
                        }}
                      >
                        <span>
                          ${tier.min.toLocaleString()} - {tier.max === Infinity ? '$75k+' : `$${tier.max.toLocaleString()}`}
                        </span>
                        <span style={{ marginLeft: '16px' }}>${isCurrentTier && onePercentFloor ? formatMinimumPayment(minimumPayment) : tier.minPayment}/mo</span>
                      </div>
                    )
                  })}
                </div>
                <div className="mt-2 pt-2 border-t text-text-secondary">
                  <div className="font-semibold">Your minimum: ${formatMinimumPayment(minimumPayment)}/month</div>
                  {onePercentSetsMinimum && (
                    <div>Above ${FDR_ONE_PERCENT_THRESHOLD.toLocaleString()} the minimum is at least 1% of enrolled.</div>
                  )}
                </div>
              </div>
            </ToolTip>
            <span>| Plan total price: ${calculateTotalAmount({
              accountBalance: totalBalanceOwed,
              programPlan,
              programLength: programPlan?.plan,
              frequency: frequency,
              startDate,
              secondStartDate,
            })?.toFixed(2) ?? '—'}</span>
          </div>
        </div>
        <div className="border-b border-divider mx-6" />
        {/* Its own row, above the two columns — inside the left column it made that column
            taller than the right and threw the Program Fee / Custodial Fee labels out of line.
            Same placement the regenerate-plan modal uses. */}
        {flatAllowed && !flatOnly && (
          <div className="px-12 pt-6">
            <FeeShapeChoice allowed={flatAllowed} isFlat={isFlat} onPick={pickFeeShape} flatOnly={flatOnly} />
          </div>
        )}
        <div className="flex px-12 gap-4">
          <div className="flex-1">
            {isFlat && flatFeeTyped ? (
              <Input
                value={flatFeeField.text}
                onChange={flatFeeField.onChange}
                errorMsg={flatFeeField.error || undefined}
                placeholder="$"
                label="Flat Fee $:"
                type="text"
                classes="mt-6"
              />
            ) : isFlat ? (
              <Dropdown placeholder="Select a Value" label="Flat Fee:"
                value={programPlan?.flatFee ?? ''}
                setValue={(e) => {
                  markRecalcRequested()
                  setProgramPlan((prev) => ({ ...prev, flatFee: Number(e) }))
                }}
                menuItems={flatFeeMenu}
                className="mt-6"
              />
            ) : (
              <ProgramFee value={programPlan?.programFee}
                setValue={(e) => {
                  manualEdits.current.programFee = true
                  markRecalcRequested()
                  setProgramPlan((prev) => ({ ...prev, programFee: e }))
                }} classes="mt-6" lowerFloor={lowerFloor} />
            )}
          </div>
          <div className="flex-1">
            <CustodialFee value={programPlan?.custodialFee}
              setValue={(e) => {
                manualEdits.current.custodialFee = true
                markRecalcRequested()
                setProgramPlan((prev) => ({ ...prev, custodialFee: e }))
              }}
              classes="mt-6" />
          </div>
        </div>
        <div className="flex px-12 pt-6 gap-4">
          <div className="flex-1">
            <Dropdown
              value={frequency}
              setValue={(e) => {
                markRecalcRequested()
                setFrequency(e)
                setProgramPlan((prev) => ({ ...prev, frequency: e, plan: null }))
                if (e === 'semiMonthly') {
                  const start = paymentSchedule?.cycleStartDate
                  if (start && dayjs(start).isValid()) {
                    setPaymentSchedule((prev) => ({ ...prev, secondDate: dayjs(start).add(15, 'day') }))
                  }
                } else {
                  setPaymentSchedule((prev) => ({ ...prev, secondDate: null }))
                }
              }}
              menuItems={PAYMENT_SCHEDULE}
              className="mt-1.5"
              variant="standard"
              placeholder="Choose frequency"
              label="Payment Frequency:"
            />
          </div>
          <div className="flex-1">
            <Input
              value={isInitialFeeDisabled ? 0 : programPlan?.initialFee}
              onChange={(e) => {
                const value = e.target.value
                if (value >= 0 || value === "") {
                  markRecalcRequested()
                  setProgramPlan((prev) => ({ ...prev, initialFee: value }))
                }
              }}
              placeholder="$"
              min="0"
              label="Enrollment Fee $:"
              type="number"
              disabled={isInitialFeeDisabled}
            />
          </div>
        </div>
        <div className="flex px-12 pt-6 gap-4">
          <div className="flex-1 mt-[-20px]">
            <Dropdown
              value={programPlan?.plan}
              setValue={(e) => {
                manualEdits.current.plan = true
                markRecalcRequested()
                setProgramPlan((prev) => ({ ...prev, plan: e }))
              }}
              menuItems={getProgramLengthMenu()}
              className="mt-3"
              variant="standard"
              placeholder="Choose plan"
              label="Program length:"
            />
          </div>
        </div>
        {showMinimumPaymentWarning && (
          <div className="px-12 pt-2">
            <div className="text-warning-main text-sm">
              ⚠️ Monthly payment is below minimum (${formatMinimumPayment(minimumPayment)}/month). Please adjust the program length or fees.
            </div>
          </div>
        )}
        <div className="w-full mt-3 px-12 mb-12">
          <Calendar
            label="Start Date"
            value={startDate}
            onChange={(e) => {
              const newDate = dayjs(e)
              markRecalcRequested()
              setPaymentSchedule((prev) => ({
                ...prev,
                cycleStartDate: newDate,
                ...(frequency === 'semiMonthly' ? { secondDate: newDate.add(15, 'day') } : {})
              }))
            }}
            isRequired={true}
            weekendsDisabled={false}
          />
        </div>
        <div className="bg-background mx-12 rounded-2xl pb-8 mb-12">
          <div className="flex justify-around pt-6 font-semibold">
            Payment Plan Rates
          </div>
          <div
            className="flex flex-col max-h-[240px] payment-rates-scroll"
            style={{
              overflowY: 'scroll',
              scrollbarWidth: 'thin',
              scrollbarColor: 'var(--color-gray-500) var(--color-gray-100)'
            }}
          >
            {!hasStartDate && (
              <div className="flex justify-center px-6 pt-4 pb-2 text-text-secondary italic">
                Pick a Start Date to see per-payment estimates
              </div>
            )}
            {hasStartDate && (() => {
              const menuItems = getProgramLengthMenu()

              let minRecommendedIndex = -1
              for (let i = 0; i < menuItems.length; i++) {
                const item = menuItems[i]
                const isValid = validateMinimumMonthlyPayment({
                  accountBalance: totalBalanceOwed,
                  programPlan,
                  programLength: item.value,
                  frequency,
                  startDate,
                  secondStartDate,
                  onePercentFloor,
                })
                if (isValid) {
                  minRecommendedIndex = i
                }
              }

              return menuItems.map((item, index) => {
                const paymentAmount = calculateMonthlyPaymentAmount({
                  accountBalance: totalBalanceOwed,
                  programLength: item.value,
                  programPlan,
                  frequency: frequency,
                  startDate,
                  secondStartDate,
                })
                const isMinRecommended = index === minRecommendedIndex

                return (
                  <div key={item.value} className={`flex justify-between px-6 pt-2 ${item.value === programPlan?.plan ? "font-bold" : ""}`}>
                    <span className="text-primary-main font-medium min-w-[60px]">
                      {isMinRecommended && "MIN →"}
                    </span>
                    <span className="flex-1 text-center">
                      {item.label} Est. ${paymentAmount?.toFixed(2)} / per payment
                    </span>
                    <span className="min-w-[60px]"></span>
                  </div>
                )
              })
            })()}
          </div>
        </div>
      </div>
      <div className="mt-6 mb-6 flex justify-end space-x-4">
        <Button
          disabled={isProgramPlanIncomplete({ programPlan, userRole, hasStartDate })}
          width="min-w-[156px]"
          onClick={async () => {
            // WS #2228897: an office that splits the fee across cards (PACK) can be asked to confirm
            // dropping the split, so the screen waits for the save there. Other offices move on at once.
            if (feeShape?.cardSplit) {
              if (await updateProgramPlan()) proceed()
              return
            }
            updateProgramPlan()
            proceed()
          }}
        >
          Save & proceed
        </Button>
      </div>
      <ConfirmModal
        title="Remove the card split?"
        modalButtonTitle="Remove the split and save"
        open={!!splitDrop}
        setOpen={(open) => { if (!open) setSplitDrop(null) }}
        functionButton={async () => {
          if (await updateProgramPlan({ dropSplit: true })) proceed()
        }}
      >
        {splitDrop}
      </ConfirmModal>
    </>
  )
}
